import React, { useMemo } from "react";
import { Separator } from "@/components/ui/separator.tsx";
import {SidebarNav} from "@/components/ui/sidebar-nav.tsx";
import {SectionTitle} from "@/components/ui/headings";
import {useTranslation} from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { getEffectiveDeadlines } from "@/services/deadlineExtensionService.ts";
import { queryKeys } from "@/services/queryKeyFactory.ts";
import { useAuth } from "@/contexts/AuthContext.tsx";
import { UserRole } from "@/types/UserRole.ts";
import { isPreferenceSubmissionPeriod, isBeforeMentorSelectionDeadline } from "@/utils/time-helper.ts";

interface EditProfileLayoutProps {
    children: React.ReactNode;
}

export default function EditProfileLayout({ children }: EditProfileLayoutProps) {
    const {t} = useTranslation();
    const {loggedInUser} = useAuth();
    const baseUrl = typeof window !== "undefined" ? window.location.href.match(/(\/mentors\/\d+\/edit)/)?.[1] ?? "" : "";

    const {data: effectiveDeadlines} = useQuery({
        queryKey: queryKeys.effectiveDeadlines,
        queryFn: getEffectiveDeadlines,
        enabled: !!loggedInUser?.id,
    });
    const submitPreferenceDeadline = effectiveDeadlines?.preferenceSubmission
        ? new Date(effectiveDeadlines.preferenceSubmission)
        : undefined;
    const mentorSelectionDeadline = effectiveDeadlines?.mentorSelection
        ? new Date(effectiveDeadlines.mentorSelection)
        : undefined;
    const isMentor = loggedInUser?.role === UserRole.MENTOR;
    const isAdmin = loggedInUser?.role === UserRole.ADMIN;
    const mentorPastDeadline = isMentor && !isPreferenceSubmissionPeriod(submitPreferenceDeadline);
    const adminPastDeadline = isAdmin && !isBeforeMentorSelectionDeadline(mentorSelectionDeadline);
    const cannotEditAvailability = mentorPastDeadline || adminPastDeadline;

    const sidebarNavItems = useMemo(() => {
        const items = [
            { title: "general", href: baseUrl },
            { title: "details", href: `${baseUrl}/details` },
            { title: "availability", href: `${baseUrl}/availability` },
        ];
        return cannotEditAvailability ? items.filter((i) => i.title !== "availability") : items;
    }, [baseUrl, cannotEditAvailability]);

    return (
        <>
            <div className="container pt-8 space-y-6 pb-12">
                <div className="space-y-0.5">
                    <SectionTitle>{t("profile.myProfile")}</SectionTitle>
                    <p className="text-text-2">
                        {t("profile.myProfileDesc")}
                    </p>
                </div>
                <Separator className="my-6" />
                <div className="flex flex-col space-y-8 lg:flex-row lg:space-x-12 lg:space-y-0">
                    <aside className="-mx-4 lg:w-1/5">
                        <SidebarNav items={sidebarNavItems} />
                    </aside>
                    <div className="flex-1 lg:max-w-2xl">{children}</div>
                </div>
            </div>
        </>
    );
}
